import { ButtonContent, PendingOverlay } from "@ui/components/internal/ButtonContent";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button, type ButtonProps } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";

export const outlineButton = tv({
  base: [
    "relative inline-flex cursor-pointer items-center justify-center rounded-full bg-white font-semibold text-neutral-600",
    "disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-300",
    "data-[pending]:cursor-default",
  ],
  compoundVariants: [
    {
      border: "default",
      shape: "pill",
      size: "36",
      class: "px-3 py-1 text-base",
    },
    {
      border: "default",
      shape: "pill",
      size: "40",
      class: "px-[18px] py-1.5 text-lg leading-6",
    },
    {
      border: "light",
      shape: "pill",
      size: "40",
      class: "px-3 py-[7px] text-base tracking-[-0.16px]",
    },
    {
      shape: "circle",
      size: "36",
      class: "size-9",
    },
    {
      shape: "circle",
      size: "40",
      class: "size-10",
    },
  ],
  defaultVariants: {
    border: "default",
    hoverColor: "neutral",
    shape: "pill",
    size: "40",
  },
  variants: {
    border: {
      default: [
        "border-2 border-neutral-100",
        "hover:border-neutral-300 hover:bg-neutral-50",
        "pressed:bg-neutral-100",
      ],
      light: ["border border-neutral-100", "hover:border-neutral-300 hover:bg-neutral-50", "pressed:bg-neutral-100"],
    },
    // Accent-tinted hover/pressed treatment layered over the outline shell
    // (e.g. the purple "start chat" affordance). Neutral keeps the border
    // variant's own hover styles.
    hoverColor: {
      neutral: "",
      purple: [
        "hover:border-purple-500 hover:bg-purple-25 hover:text-purple-500",
        "pressed:border-purple-500 pressed:bg-purple-50 pressed:text-purple-500",
      ],
    },
    shape: {
      circle: "p-0",
      pill: "",
    },
    size: {
      "36": "gap-1.5 [&_svg]:size-4",
      "40": "gap-2 [&_svg]:size-5",
    },
  },
});

type OutlineButtonVariants = VariantProps<typeof outlineButton>;

export type OutlineButtonProps = ButtonProps & {
  border?: OutlineButtonVariants["border"];
  hoverColor?: OutlineButtonVariants["hoverColor"];
  leadIcon?: ReactNode;
  shape?: OutlineButtonVariants["shape"];
  size?: OutlineButtonVariants["size"];
  trailIcon?: ReactNode;
};

export function OutlineButton({ border, hoverColor, leadIcon, shape, size, trailIcon, ...props }: OutlineButtonProps) {
  return (
    <Button
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        outlineButton({ ...renderProps, border, hoverColor, shape, size, className }),
      )}
    >
      {composeRenderProps(props.children, (children, { isPending }) => (
        <>
          <ButtonContent isPending={isPending} leadIcon={leadIcon} trailIcon={trailIcon}>
            {children}
          </ButtonContent>
          <PendingOverlay isPending={isPending} />
        </>
      ))}
    </Button>
  );
}
